import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { AVATAR_ICONS } from "@/components/Bits";
import { PERIODS, PHASES, ADV_LABELS, Select } from "@/lib/periods";

const Num = ({ label, value, onChange, testId, min, max }) => (
  <label className="block text-xs text-slate-400">{label}
    <input type="number" min={min} max={max} value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={testId} className="mpl-input mt-1" />
  </label>
);

function PeriodEmblem({ league, reload, perms }) {
  const [p, setP] = useState(league.main_period || { type: "month", phase: "full" });
  const save = async (body) => { try { await api.patch(`/leagues/${league.id}`, body); toast.success("Enregistré"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className="mpl-card space-y-4 p-5">
      {perms.info && <div>
        <p className="mpl-eyebrow mb-2">Emblème</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(AVATAR_ICONS).map(([k, Icon]) => (
            <button key={k} type="button" onClick={() => save({ emblem: k })} data-testid={`emblem-${k}`} aria-label={k}
              className={`flex h-10 w-10 items-center justify-center rounded-xl border ${league.emblem === k ? "border-[#B7F52A] bg-[#B7F52A] text-[#05070D]" : "border-white/10 text-slate-300"}`}><Icon size={18} /></button>
          ))}
        </div>
      </div>}
      {perms.rules && <div>
        <p className="mpl-eyebrow mb-2">Période principale {league.competition_started && "(figée)"}</p>
        <div className="grid gap-2 sm:grid-cols-3">
          <Select value={p.type} onChange={(v) => setP({ ...p, type: v })} options={PERIODS} testId="main-period-type" />
          {p.type === "nba_season" && <Select value={p.phase || "full"} onChange={(v) => setP({ ...p, phase: v })} options={PHASES} testId="main-period-phase" />}
          {p.type === "custom" && (<>
            <input type="date" className="mpl-input" value={p.start || ""} onChange={(e) => setP({ ...p, start: e.target.value })} data-testid="main-period-start" />
            <input type="date" className="mpl-input" value={p.end || ""} onChange={(e) => setP({ ...p, end: e.target.value })} data-testid="main-period-end" />
          </>)}
        </div>
        {!league.competition_started && <button onClick={() => save({ main_period: { type: p.type, phase: p.phase || "full", ...(p.type === "custom" ? { start: p.start, end: p.end } : {}) } })} className="mpl-btn-ghost mt-3" data-testid="main-period-save">Enregistrer la période</button>}
      </div>}
    </section>
  );
}

export default function RulesEditor({ league, reload, perms = { rules: true, info: true } }) {
  const r = league.scoring_rule;
  const [f, setF] = useState({ correct_points: r.correct_points, wrong_points: r.wrong_points, streak_bonuses: r.streak_bonuses.map(({ at, points }) => ({ at, points })),
    max_advanced: r.max_advanced, advanced_types: r.advanced_types, advanced_points: r.advanced_points });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => { try { await api.put(`/leagues/${league.id}/rules`, f); toast.success("Barème enregistré (nouvelle version)"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const toggleType = (t) => set("advanced_types", f.advanced_types.includes(t) ? f.advanced_types.filter((x) => x !== t) : [...f.advanced_types, t]);
  return (
    <div className="space-y-4">
      <PeriodEmblem league={league} reload={reload} perms={perms} />
      {league.rules_editable && perms.rules && (
        <section className="mpl-card space-y-4 p-5" data-testid="rules-editor">
          <p className="mpl-eyebrow">Barème personnalisé (modifiable jusqu'au démarrage)</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Num label="Bonne prédiction" value={f.correct_points} min={1} max={50} onChange={(v) => set("correct_points", v)} testId="rule-correct" />
            <Num label="Mauvaise prédiction (0 ou négatif)" value={f.wrong_points} min={-50} max={0} onChange={(v) => set("wrong_points", v)} testId="rule-wrong" />
          </div>
          {f.streak_bonuses.map((b, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
              <Num label="Série de" value={b.at} min={2} max={20} onChange={(v) => set("streak_bonuses", f.streak_bonuses.map((x, j) => (j === i ? { ...x, at: v } : x)))} testId={`rule-bonus-at-${i}`} />
              <Num label="Bonus" value={b.points} min={0} max={50} onChange={(v) => set("streak_bonuses", f.streak_bonuses.map((x, j) => (j === i ? { ...x, points: v } : x)))} testId={`rule-bonus-pts-${i}`} />
              <button type="button" onClick={() => set("streak_bonuses", f.streak_bonuses.filter((_, j) => j !== i))} className="mpl-btn-ghost">Retirer</button>
            </div>
          ))}
          {f.streak_bonuses.length < 3 && <button type="button" onClick={() => set("streak_bonuses", [...f.streak_bonuses, { at: 5, points: 5 }])} className="mpl-btn-ghost text-xs" data-testid="rule-bonus-add">+ Palier de série</button>}
          <Num label="Pronostics bonus par match (0 à 3)" value={f.max_advanced} min={0} max={3} onChange={(v) => set("max_advanced", v)} testId="rule-max-advanced" />
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(ADV_LABELS).map(([t, l]) => (
              <button type="button" key={t} onClick={() => toggleType(t)} data-testid={`rule-adv-${t}`} className={`rounded-full border px-3 py-1 text-xs ${f.advanced_types.includes(t) ? "border-[#B7F52A] text-[#B7F52A]" : "border-white/10 text-slate-400"}`}>{l}</button>
            ))}
          </div>
          <button onClick={save} className="mpl-btn-primary" data-testid="rules-save">Enregistrer le barème</button>
        </section>
      )}
    </div>
  );
}
